// Boomin' — Onboarding Overlay
// SEO-safe: the underlying Top screen is fully rendered HTML; this is a JS-mounted
// dimmer + welcome sheet that appears on first visit only.

import React from "react";
import {
  BRAND,
  BRAND_SOFT,
  ACCENT,
  PictX,
  ConfettiBurst,
  Sparkle,
  useBoominFx,
} from "./shared";

const brand = BRAND;
const brandSoft = BRAND_SOFT;
const accent = ACCENT;

type Props = { onDismiss: () => void };

export const OnboardingOverlay: React.FC<Props> = ({ onDismiss }) => {
  useBoominFx();
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        overflow: "hidden",
        pointerEvents: "auto",
      }}
    >
      {/* Dimmer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(20,8,0,0.35) 0%, rgba(20,8,0,0.55) 100%)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          zIndex: 1,
        }}
        onClick={onDismiss}
      />

      {/* Confetti above dimmer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        <ConfettiBurst
          density={22}
          palette={[brand, accent, "#FFE05A", "#FFC9B0", "#FFFFFF"]}
        />
      </div>

      {/* Bottom sheet */}
      <div
        data-boomin-anim
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3,
          background: "#fff",
          borderRadius: "28px 28px 0 0",
          padding: "20px 24px calc(env(safe-area-inset-bottom, 0px) + 36px)",
          boxShadow: "0 -20px 44px rgba(0,0,0,0.22)",
          animation: "boom-rise 380ms ease-out",
          maxWidth: 640,
          margin: "0 auto",
        }}
      >
        {/* Grip */}
        <div
          style={{
            width: 36,
            height: 4,
            background: "#E5DBD0",
            borderRadius: 999,
            margin: "0 auto 16px",
          }}
        />

        {/* Mini ticket lockup */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 14,
            position: "relative",
            height: 64,
          }}
        >
          <div
            data-boomin-anim
            style={{
              position: "absolute",
              left: "50%",
              top: 0,
              transform: "translateX(-50%) rotate(-6deg)",
              animation:
                "boom-fly-up 480ms ease-out, boom-wiggle 4s 600ms ease-in-out infinite",
              width: 156,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 10px 24px rgba(0,0,0,0.14)",
                border: "1px solid #F0E6DC",
                overflow: "hidden",
                display: "flex",
              }}
            >
              <div
                style={{
                  width: 16,
                  background: `linear-gradient(180deg, ${brand} 0%, ${accent} 100%)`,
                }}
              />
              <div style={{ flex: 1, padding: "8px 10px" }}>
                <div style={{ fontSize: 8, color: "#999", fontWeight: 700 }}>
                  ECO FARM CAFE 632
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    marginTop: 2,
                    color: "#1A1A1A",
                  }}
                >
                  ランチオプション
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: brand,
                    lineHeight: 1,
                  }}
                >
                  無料 クーポン
                </div>
              </div>
            </div>
          </div>
          <Sparkle style={{ top: -4, left: "28%" }} delay={120} color={brand} size={14} />
          <Sparkle style={{ top: 8, right: "24%" }} delay={420} color="#FFC325" size={12} />
          <Sparkle style={{ top: 36, left: "18%" }} delay={760} color="#3DBED9" size={10} />
        </div>

        <h2
          style={{
            fontSize: 26,
            fontWeight: 800,
            lineHeight: 1.2,
            margin: 0,
            letterSpacing: "0.01em",
            textAlign: "center",
          }}
        >
          ようこそ、
          <br />
          <span style={{ color: brand }}>Boomin'</span> へ。
        </h2>
        <p
          style={{
            fontSize: 13,
            color: "#666",
            textAlign: "center",
            margin: "12px 0 0",
            lineHeight: 1.7,
          }}
        >
          いま、このあたりで使えるクーポンが
          <br />
          サクッと見つかります。
        </p>

        <div
          style={{
            marginTop: 20,
            padding: "12px 14px",
            background: brandSoft,
            borderRadius: 14,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PictX name="locate" size={18} stroke={brand} strokeWidth={1.9} />
          </div>
          <div style={{ fontSize: 12, color: "#555", lineHeight: 1.5 }}>
            <b>位置情報を許可</b>すると、徒歩10分の
            <br />
            お店だけにしぼって表示できます
          </div>
        </div>

        <button
          data-boomin-anim
          onClick={() => {
            if ("geolocation" in navigator) {
              navigator.geolocation.getCurrentPosition(
                () => onDismiss(),
                () => onDismiss()
              );
            } else {
              onDismiss();
            }
          }}
          style={{
            width: "100%",
            marginTop: 20,
            height: 54,
            borderRadius: 999,
            border: 0,
            background: brand,
            color: "#fff",
            fontSize: 15,
            fontWeight: 800,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            boxShadow: `0 12px 26px ${brand}55, 0 1px 0 rgba(255,255,255,0.4) inset`,
            animation: "boom-pulse 2.4s ease-in-out infinite",
            fontFamily: "inherit",
          }}
        >
          位置情報を許可してはじめる
          <PictX name="chevron" size={16} stroke="#fff" strokeWidth={2.4} />
        </button>
        <button
          onClick={onDismiss}
          style={{
            width: "100%",
            marginTop: 8,
            padding: "10px 0",
            background: "transparent",
            border: 0,
            color: "#888",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          あとで（クーポンだけ見る）
        </button>
      </div>
    </div>
  );
};

export default OnboardingOverlay;
